import type { TileChart } from "./trends";

// The small charts of the Home tiles (CHT-2025): a line and a row of bars, drawn as SVG with palette colours (the text colour, the soft
// grey and the brass of the Concierge). They are decoration: they hide from a screen reader, and the tile writes every number as text.
// They stretch to the width of the tile (`preserveAspectRatio="none"`), so the stroke keeps its width (`vector-effect`).

const WIDTH = 160;
const HEIGHT = 32;
/** The stroke needs room at the top and the bottom of the box. */
const PAD = 2;
const BAR_GAP = 5;
/** A bar of zero still shows a thin stub, so a quiet day is a day and not a hole. */
const MIN_BAR = 2;

const scaleOf = (values: readonly number[]) => {
  const low = Math.min(...values);
  const high = Math.max(...values);
  return { low, span: high - low };
};

/** The points of the line, the oldest day at the left. A flat series (the same number each day) is a line across the middle. */
function linePoints(values: readonly number[]): string {
  const { low, span } = scaleOf(values);
  const step = values.length > 1 ? WIDTH / (values.length - 1) : 0;
  return values
    .map((value, index) => {
      const y =
        span === 0 ? HEIGHT / 2 : HEIGHT - PAD - ((value - low) / span) * (HEIGHT - 2 * PAD);
      return `${Math.round(index * step * 10) / 10},${Math.round(y * 10) / 10}`;
    })
    .join(" ");
}

function Line({ values }: Readonly<{ values: readonly number[] }>) {
  return (
    <polyline
      points={linePoints(values)}
      fill="none"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      vectorEffect="non-scaling-stroke"
      className="stroke-foreground"
    />
  );
}

/** Bars share one scale that starts at zero. Today is the last bar and is brass. */
function Bars({ values }: Readonly<{ values: readonly number[] }>) {
  const high = Math.max(...values, 0);
  const width = (WIDTH - BAR_GAP * (values.length - 1)) / values.length;
  return (
    <>
      {values.map((value, index) => {
        const height = high === 0 ? MIN_BAR : Math.max(MIN_BAR, (value / high) * HEIGHT);
        const today = index === values.length - 1;
        return (
          <rect
            // The days are a fixed list of 7: the position is the identity.
            // biome-ignore lint/suspicious/noArrayIndexKey: a series has no ids
            key={index}
            x={index * (width + BAR_GAP)}
            y={HEIGHT - height}
            width={width}
            height={height}
            rx={2}
            className={today ? "fill-brass" : "fill-neutral-soft"}
          />
        );
      })}
    </>
  );
}

export function TrendChart({ chart }: Readonly<{ chart: TileChart }>) {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      width="100%"
      height={HEIGHT}
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      preserveAspectRatio="none"
      className="block shrink-0 overflow-visible"
    >
      {chart.kind === "line" ? <Line values={chart.values} /> : <Bars values={chart.values} />}
    </svg>
  );
}
